import { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, XCircle, IndianRupee, RefreshCw, Inbox } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const NAVY = '#16335b';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function DhfPaymentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [acting, setActing] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get('/dashboard/health-file/upi/pending');
      setRows(data.pending || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Could not load payments'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (token, kind) => {
    setActing(token + kind); setErr('');
    try {
      await api.post(`/dashboard/health-file/${token}/upi/${kind}`);
      setRows((r) => r.filter((x) => x.view_token !== token));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Action failed'); }
    finally { setActing(''); }
  };

  return (
    <div className="w-full" data-testid="dhf-payments-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>DHF Payments to confirm</h2>
          <p className="text-sm text-slate-500 mt-0.5">Approve manual UPI payments to unlock patients' Digital Health Files.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="dhf-payments-refresh">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">{err}</div>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: NAVY }} /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="dhf-payments-empty">
          <Inbox size={32} className="text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 font-medium">No payments waiting for confirmation.</p>
          <p className="text-[13px] text-slate-400 mt-1">When a patient pays ₹20 by UPI and uploads their screenshot, it shows up here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-4" data-testid="dhf-payment-row">
              <button onClick={() => setPreview(r.proof_data_url)} className="shrink-0">
                <img src={r.proof_data_url} alt="UPI proof" className="w-24 h-24 object-cover rounded-xl border border-slate-200 hover:opacity-90" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-emerald-700 font-bold"><IndianRupee size={15} /><span>20 · DHF unlock</span></div>
                <p className="font-mono text-[12px] text-slate-500 mt-0.5 truncate">{r.file_id}</p>
                {r.txn_ref && <p className="text-[12px] text-slate-600 mt-0.5">Ref: <span className="font-mono">{r.txn_ref}</span></p>}
                <p className="text-[11px] text-slate-400 mt-0.5">{fmt(r.created_at)}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => act(r.view_token, 'approve')} disabled={!!acting}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-white text-[13px] font-bold disabled:opacity-60" style={{ background: NAVY }} data-testid="dhf-payment-approve">
                    {acting === r.view_token + 'approve' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve
                  </button>
                  <button onClick={() => act(r.view_token, 'reject')} disabled={!!acting}
                    className="px-3 py-2 rounded-full bg-white border border-rose-200 text-rose-600 text-[13px] font-semibold hover:bg-rose-50 disabled:opacity-60" data-testid="dhf-payment-reject">
                    <XCircle size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="dhf-payment-preview">
          <img src={preview} alt="UPI proof full" className="max-h-[85vh] max-w-full rounded-xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
